<template>
  <div class="dashboard">
    <el-container>
      <!-- 侧边栏 -->
      <el-aside width="250px" class="sidebar">
        <div class="logo">
          <img src="@/assets/logo.svg" alt="FanqianAI" />
          <h2>FanqianAI 管理后台</h2>
        </div>
        
        <el-menu
          :default-active="activeMenu"
          class="sidebar-menu"
          @select="handleMenuSelect"
          background-color="#304156"
          text-color="#bfcbd9"
          active-text-color="#409EFF"
        >
          <el-menu-item index="overview">
            <el-icon><DataBoard /></el-icon>
            <span>数据概览</span>
          </el-menu-item>
          
          <el-menu-item index="cards">
            <el-icon><CreditCard /></el-icon>
            <span>卡密管理</span>
          </el-menu-item>
          
          <el-sub-menu index="users">
            <template #title>
              <el-icon><User /></el-icon>
              <span>用户管理</span>
            </template>
            <el-menu-item index="users-list">用户列表</el-menu-item>
            <el-menu-item index="users-online">在线用户</el-menu-item>
          </el-sub-menu>
          
          <el-menu-item index="agents">
            <el-icon><UserFilled /></el-icon>
            <span>代理商管理</span>
          </el-menu-item>
          
          <el-sub-menu index="logs">
            <template #title>
              <el-icon><Document /></el-icon>
              <span>日志管理</span>
            </template>
            <el-menu-item index="logs-auth">验证日志</el-menu-item>
            <el-menu-item index="logs-system">系统日志</el-menu-item>
          </el-sub-menu>
          
          <el-menu-item index="settings">
            <el-icon><Setting /></el-icon>
            <span>系统设置</span>
          </el-menu-item>
        </el-menu>
      </el-aside>
      
      <!-- 主内容区 -->
      <el-container>
        <!-- 顶部导航 -->
        <el-header class="header">
          <div class="header-left">
            <el-breadcrumb separator="/">
              <el-breadcrumb-item>{{ currentPageTitle }}</el-breadcrumb-item>
            </el-breadcrumb>
          </div>
          
          <div class="header-right">
            <el-dropdown @command="handleUserCommand">
              <span class="user-info">
                <el-icon><Avatar /></el-icon>
                {{ userInfo.username }}
                <el-icon class="el-icon--right"><ArrowDown /></el-icon>
              </span>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="profile">个人资料</el-dropdown-item>
                  <el-dropdown-item command="password">修改密码</el-dropdown-item>
                  <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </el-header>
        
        <!-- 主要内容 -->
        <el-main class="main-content">
          <router-view />
        </el-main>
      </el-container>
    </el-container>

    <!-- 个人资料对话框 -->
    <ProfileDialog v-model="showProfileDialog" />

    <!-- 修改密码对话框 -->
    <PasswordDialog v-model="showPasswordDialog" />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { ElMessage, ElMessageBox } from 'element-plus'
import ProfileDialog from '@/components/ProfileDialog.vue'
import PasswordDialog from '@/components/PasswordDialog.vue'
import { authAPI } from '@/services/api'
import {
  DataBoard,
  CreditCard,
  User,
  UserFilled,
  Document,
  Setting,
  Avatar,
  ArrowDown
} from '@element-plus/icons-vue'

export default {
  name: 'DashboardPage',
  components: {
    DataBoard,
    CreditCard,
    User,
    UserFilled,
    Document,
    Setting,
    Avatar,
    ArrowDown,
    ProfileDialog,
    PasswordDialog
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const store = useStore()

    const activeMenu = ref('overview')
    const currentPageTitle = ref('数据概览')
    const showProfileDialog = ref(false)
    const showPasswordDialog = ref(false)

    const userInfo = computed(() => store.state.auth.user || {
      username: 'admin',
      role: 'super_admin'
    })
    

    
    const menuTitles = {
      'overview': '数据概览',
      'cards': '卡密管理',
      'agents': '代理商管理',
      'users-list': '用户列表',
      'users-online': '在线用户',
      'logs-auth': '验证日志',
      'logs-system': '系统日志',
      'settings': '系统设置'
    }
    
    const handleMenuSelect = (index) => {
      activeMenu.value = index
      currentPageTitle.value = menuTitles[index] || '未知页面'

      // 处理页面跳转
      switch (index) {
        case 'overview':
          router.push('/dashboard/overview')
          break
        case 'cards':
          router.push('/dashboard/cards')
          break
        case 'agents':
          router.push('/dashboard/agents')
          break
        case 'users-list':
          router.push('/dashboard/users')
          break
        case 'users-online':
          router.push('/dashboard/online-users')
          break
        case 'logs-auth':
          router.push('/dashboard/verify-logs')
          break
        case 'logs-system':
          router.push('/dashboard/system-logs')
          break
        case 'settings':
          router.push('/dashboard/settings')
          break
      }
    }
    
    const handleUserCommand = (command) => {
      switch (command) {
        case 'profile':
          showProfileDialog.value = true
          break
        case 'password':
          showPasswordDialog.value = true
          break
        case 'logout':
          handleLogout()
          break
      }
    }
    
    const handleLogout = async () => {
      try {
        await ElMessageBox.confirm('确定要退出登录吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        try {
          // 调用后端退出登录API
          await authAPI.logout()
        } catch (error) {
          console.error('后端退出登录失败:', error)
          // 即使后端失败，也继续前端退出流程
        }

        await store.dispatch('auth/logout')
        ElMessage.success('退出成功')
        router.push('/login')
      } catch (error) {
        if (error !== 'cancel') {
          console.error('退出登录失败:', error)
        }
      }
    }
    

    
    // 根据当前路由设置activeMenu
    const updateActiveMenu = () => {
      const path = route.path
      if (path.includes('/overview')) {
        activeMenu.value = 'overview'
        currentPageTitle.value = '数据概览'
      } else if (path.includes('/cards')) {
        activeMenu.value = 'cards'
        currentPageTitle.value = '卡密管理'
      } else if (path.includes('/agents')) {
        activeMenu.value = 'agents'
        currentPageTitle.value = '代理商管理'
      } else if (path.includes('/users') && !path.includes('/online-users')) {
        activeMenu.value = 'users-list'
        currentPageTitle.value = '用户列表'
      } else if (path.includes('/online-users')) {
        activeMenu.value = 'users-online'
        currentPageTitle.value = '在线用户'
      } else if (path.includes('/user-stats')) {
        activeMenu.value = 'users-list'
        currentPageTitle.value = '用户统计'
      } else if (path.includes('/verify-logs')) {
        activeMenu.value = 'logs-auth'
        currentPageTitle.value = '验证日志'
      } else if (path.includes('/system-logs')) {
        activeMenu.value = 'logs-system'
        currentPageTitle.value = '系统日志'
      }
    }

    // 监听路由变化
    watch(() => route.path, () => {
      updateActiveMenu()
    }, { immediate: true })

    // 监听路由变化
    watch(() => route.path, () => {
      updateActiveMenu()
    }, { immediate: true })

    onMounted(() => {
      updateActiveMenu()
    })

    return {
      activeMenu,
      currentPageTitle,
      userInfo,
      showProfileDialog,
      showPasswordDialog,
      handleMenuSelect,
      handleUserCommand
    }
  }
}
</script>

<style scoped>
.dashboard {
  height: 100vh;
  overflow: hidden;
}

.dashboard .el-container {
  height: 100vh;
}

.sidebar {
  background-color: #304156;
  color: white;
  height: 100vh;
  overflow-y: auto;
}

.logo {
  padding: 20px;
  text-align: center;
  border-bottom: 1px solid #434a50;
}

.logo img {
  width: 40px;
  height: 40px;
  margin-bottom: 10px;
}

.logo h2 {
  margin: 0;
  font-size: 16px;
  color: #bfcbd9;
}

.sidebar-menu {
  border: none;
}

.header {
  background: white;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
}

.user-info {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.main-content {
  background: #f0f2f5;
  padding: 20px;
  height: calc(100vh - 60px);
  overflow-y: auto;
}

.stat-card {
  text-align: center;
}

.stat-value {
  font-size: 32px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 8px;
}

.stat-label {
  color: #666;
  font-size: 14px;
}

.system-status {
  padding: 20px 0;
}

.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.content-placeholder {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
}
</style>
